// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import awsExports from './aws-exports';
import { AuthProvider } from './AuthContext';
import './styles/CinematicTheme.css';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import HostedUILogin from './components/HostedUILogin';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import IOSAppBanner from './components/IOSAppBanner';
import OAuthCallbackHandler from './components/OAuthCallbackHandler';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPage from './pages/AdminPage';
import { StripeProvider } from './StripeContext';

// Import pages
import HomePage from './pages/HomePage';
import TripListPage from './pages/TripListPage';
import TripDetailPage from './pages/TripDetailPage';
import CreateTripPage from './pages/CreateTripPage';
import UserProfilePage from './pages/UserProfilePage';
import { HelmetProvider } from 'react-helmet-async';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfUsePage from './components/TermsOfUsePage';



// Configure Amplify
Amplify.configure(awsExports);

// Add this component in your App.tsx
const CaptureRedirect = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect');

    if (redirectUrl) {
      localStorage.setItem('authRedirectUrl', redirectUrl);
    }
  }, []);
  console.log("Exiting capture redirect");
  return null; // This component doesn't render anything
};


// Theme with brand colors
// src/App.tsx - Update the theme object

const theme = {
  name: 'banjara-cinematic-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#1A1A1A',  // Almost black
          20: '#202020',
          40: '#303030',
          60: '#505050',
          80: '#707070',
          90: '#909090',
          100: '#0073EA', // Keep a touch of blue for highlights
        },
        secondary: {
          80: '#2C5282', // Dark blue for accents
        }
      },
      background: {
        primary: '#121212',
        secondary: '#1A1A1A',
      },
      font: {
        primary: '#FFFFFF',
        secondary: '#AAAAAA',
        tertiary: '#777777',
      },
      border: {
        primary: '#303030',
        secondary: '#202020',
      }
    },
    radii: {
      small: '2px',
      medium: '4px',
      large: '6px',
    },
    space: {
      small: '8px',
      medium: '16px',
      large: '24px',
    },
    fontWeights: {
      light: 300,
      normal: 400,
      bold: 700,
    },
    fonts: {
      default: {
        variable: { value: "'Inter', 'Helvetica Neue', sans-serif" },
        static: { value: "'Inter', 'Helvetica Neue', sans-serif" },
      },
    },
  }
};
function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <StripeProvider>

          <HelmetProvider>
            <div className="cinematic-container min-h-screen">
              <IOSAppBanner />
              <Router>
                <Routes>
                  <Route path="/auth/callback/" element={<OAuthCallbackHandler />} />
                  <Route path="/login" element={
                    <>
                      <CaptureRedirect />
                      <HostedUILogin />
                    </>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <Header />
                      <AdminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/" element={
                    <>
                      <Header />
                      <HomePage />
                      <Footer />
                    </>
                  } />

                  <Route path="/trips" element={
                    <ProtectedRoute>
                      <Header />
                      <TripListPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/trips/new" element={
                    <ProtectedRoute>
                      <Header />
                      <CreateTripPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/trips/:tripId" element={
                    <ProtectedRoute>
                      <Header />
                      <TripDetailPage />
                    </ProtectedRoute>
                  } />


                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Header />
                      <UserProfilePage />
                      <Footer />
                    </ProtectedRoute>
                  } />
                  <Route path="/privacy-policy" element={
                    <>
                      <Header />
                      <PrivacyPolicyPage />
                    </>
                  } />
                  <Route path="/terms-of-use" element={
                    <>
                      <Header />
                      <TermsOfUsePage />
                      <Footer />
                    </>
                  } />

                  {/* Default route for any undefined paths */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Router>
            </div>
          </HelmetProvider>
        </StripeProvider>
      </AuthProvider>
    </ThemeProvider >
  );
}

export default App;